import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { lint } from 'markdownlint/sync';
import CodeEditor from '@uiw/react-textarea-code-editor';
import rehypePrism from 'rehype-prism-plus';

import { BodyWrapper, Header, Loader, MainWrapper, Message } from '@components';
import { siteQuery, siteMutation } from '@queries';
import { ButtonSpinnerIcon } from '@svgs';

import SiteSidebar from '../components/SiteSidebar/SiteSidebar.jsx';
import { SiteBreadcrumbs } from '../components/SiteBreadcrumbs/SiteBreadcrumbs.jsx';

import '@uiw/react-textarea-code-editor/dist.css';
import './SEOPage.scss';

const markdownEditorPlugins = [
  [rehypePrism, { ignoreMissing: true, showLineNumbers: true }],
];

const MARKDOWN_KEY = 'content';

const MARKDOWN_LINT_CONFIG = {
  MD041: false,
  MD047: false,
  MD022: false,
  MD013: false,
  MD009: false,
  MD032: false,
};

function formatLintErrors(lintResults) {
  return (lintResults[MARKDOWN_KEY] || []).map(
    ({ lineNumber, ruleNames, ruleDescription, errorDetail }) => {
      const rule = ruleNames?.[0] || 'unknown';
      const detail = errorDetail ? ` — ${errorDetail}` : '';
      const adjustedLineNumber = Math.max(1, lineNumber);
      return `Line ${adjustedLineNumber}: ${ruleDescription} (${rule})${detail}`;
    },
  );
}

function lintMarkdown(markdown) {
  const results = lint({
    strings: { [MARKDOWN_KEY]: markdown },
    config: MARKDOWN_LINT_CONFIG,
  });

  return formatLintErrors(results);
}

export const SEOPage = () => {
  const { siteID } = useParams();
  const queryClient = useQueryClient();

  const [markdown, setMarkdown] = useState('');
  const [validationState, setValidationState] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationMessageKey, setValidationMessageKey] = useState(0);
  const [isValidating, setIsValidating] = useState(false);

  const { data: siteData, isPending: siteIsPending } = useQuery(
    siteQuery(siteID),
  );

  useEffect(() => {
    if (siteData) {
      setMarkdown(siteData.llms_txt || '');
    }
  }, [siteData]);
  const mutation = useMutation(
    siteMutation(siteID, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ['sites', 'detail', siteID],
        });
      },
      onError: (error) => {
        console.error({ error });
      },
    }),
  );

  const handleSave = () => {
    setValidationMessageKey((key) => key + 1);
    setIsValidating(true);
    setValidationState(null);
    setValidationErrors([]);

    try {
      console.log({ markdown });
      const errors = lintMarkdown(markdown);
      if (errors.length > 0) {
        setValidationState('error');
        setValidationErrors(errors);
      } else {
        setValidationState('success');
        mutation.mutate({
          llms_txt: markdown,
        });
      }
    } catch (error) {
      setValidationState('error');
      setValidationErrors([
        error instanceof Error ? error.message : 'Failed to validate markdown',
      ]);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <>
      <Header />
      <BodyWrapper>
        <SiteSidebar siteID={siteID} siteData={siteData} />
        <MainWrapper>
          <SiteBreadcrumbs siteData={siteData}>
            <li className="sb-flex">
              <span>AI/SEO</span>
            </li>
          </SiteBreadcrumbs>
          <div className="sb-manage-content">
            {siteIsPending && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}

            {siteData && (
              <>
                <h1>AI/SEO</h1>
                <form
                  className="sb-manage-content-form"
                  onSubmit={(event) => event.preventDefault()}
                >
                  <div className="sb-form-item-formgroup sb-form-item-input">
                    <label htmlFor="seo-markdown">llms.txt content</label>
                    <div className="sb-form-item-content">
                      <CodeEditor
                        id="seo-markdown"
                        value={markdown}
                        language="markdown"
                        placeholder="Enter raw markdown..."
                        onChange={(event) => setMarkdown(event.target.value)}
                        padding={10}
                        minHeight={420}
                        data-color-mode="light"
                        rehypePlugins={markdownEditorPlugins}
                        className="sb-seo-markdown-editor"
                      />
                      <p className="sb-form-helptext">
                        This content is appended to the automatically generated
                        llms.txt file within your website which contains key
                        information about locations and URLs
                      </p>
                    </div>
                  </div>
                  <div className="sb-manage-content-button-bar">
                    <div className="sb-flex">
                      <button
                        type="button"
                        className="sb-btn sb-btn-save"
                        disabled={isValidating || mutation.isPending}
                        onClick={handleSave}
                      >
                        {isValidating
                          ? 'Validating...'
                          : mutation.isPending
                            ? 'Saving...'
                            : 'Save'}
                        {(isValidating || mutation.isPending) && (
                          <ButtonSpinnerIcon />
                        )}
                      </button>
                    </div>
                    {validationState === 'error' && (
                      <Message
                        key={validationMessageKey}
                        messageStyle="error"
                        title="Markdown validation failed"
                        fadeoutMessage={false}
                        closable
                      >
                        {validationErrors.map((error, index) => (
                          <span key={index}>
                            {index > 0 && <br />}
                            {error}
                          </span>
                        ))}
                      </Message>
                    )}
                    {validationState === 'success' && (
                      <Message
                        messageStyle="success"
                        title="Markdown validated successfully"
                      />
                    )}
                  </div>
                </form>
              </>
            )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
